/**
 * Datora Conditional Discount Modal
 *
 * After a product is added to the cart (variant:added), queries co2.conditionalDiscounts
 * for the just-added variant. If eligible discounted products exist, shows a modal
 * on top of the open cart drawer with a Flickity slider of product cards.
 *
 * Data flow:
 *   1. variant:added  → stores variant stub (product_id, variant_id)
 *   2. cart:refresh    → parses mini-cart section HTML to extract tags + compare_at_price
 *                        via data attributes on <line-item>, then calls initConditionalModal
 *
 * Entry point: document 'variant:added' + 'cart:refresh' events
 * Reuses: existing #upsell-modal + data-upsell-trigger mechanism (custom.js)
 * Shared: window.datoraGetStorefrontClient, window.datoraSafeFormatPrice (from datora-upsell.js)
 */

window.addEventListener('DOMContentLoaded', function () {
  if (!window.cfConditionalUpsellEnabled) return;

  let flickityInstance = null;
  let wasOpen = false;
  let pendingVariant = null;

  // ─── 1. Entry point ────────────────────────────────────────

  document.addEventListener('variant:added', function (e) {
    const modal = document.getElementById('conditional-discount-modal');
    if (modal && !modal.hidden) {
      wasOpen = true;
      pendingVariant = null;
      closeConditionalModal(openConditionalCart);
      return;
    }

    const variant = e.detail && e.detail.variant;
    if (!variant) return;

    wasOpen = true;
    pendingVariant = variant;
  });

  // cart:refresh carries the sections HTML — extract tags + compare_at_price, then init
  document.documentElement.addEventListener('cart:refresh', function (e) {
    if (!pendingVariant) return;

    const variant = pendingVariant;
    pendingVariant = null;

    const sectionsHtml = e.detail && e.detail.cart && e.detail.cart.sections && e.detail.cart.sections['mini-cart'];
    const enriched = enrichVariantFromSections(variant, sectionsHtml);

    initConditionalModal(enriched);
  });

  function enrichVariantFromSections(variant, html) {
    const enriched = {
      product_id: variant.product_id,
      id: variant.id,
      tags: [],
      compare_at_price: null,
    };

    if (!html) return enriched;

    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const lineItem = doc.querySelector('line-item[data-variant-id="' + enriched.id + '"]');
      if (!lineItem) return enriched;

      const tagsAttr = lineItem.getAttribute('data-product-tags');
      if (tagsAttr) {
        enriched.tags = tagsAttr.split(',').filter(function (t) { return t.length > 0; });
      }

      const compareAttr = lineItem.getAttribute('data-compare-at-price');
      if (compareAttr) {
        enriched.compare_at_price = parseInt(compareAttr, 10) || null;
      }
    } catch (e) {
      // parsing failed — proceed with defaults
    }

    return enriched;
  }

  async function initConditionalModal(variant) {
    const settings = getConditionalSettings();
    if (!settings) { wasOpen = false; return; }

    try {
      const candidates = await getConditionalCandidates(variant);
      if (!candidates || !candidates.length) {
        wasOpen = false;
        openConditionalCart();
        return;
      }
      renderConditionalModal(candidates, settings);
      openConditionalModal();
    } catch (e) {
      wasOpen = false;
    }
  }

  // ─── 2. Settings ───────────────────────────────────────────

  function getConditionalSettings() {
    try {
      const el = document.getElementById('conditional-modal-settings');
      return el ? JSON.parse(el.textContent) : null;
    } catch (e) {
      return null;
    }
  }

  // ─── 3. Data collection ────────────────────────────────────

  async function getConditionalCandidates(variant) {
    if (typeof co2 === 'undefined') return [];

    const salePrice = !!(variant.compare_at_price && variant.compare_at_price > 0);

    const result = await co2.conditionalDiscounts(
      variant.product_id,
      variant.id,
      variant.tags || [],
      false,
      salePrice
    );

    if (!result || !result.condition_applies) return [];

    const productIds = [];
    const seen = new Set();
    for (let i = 0; i < result.condition_applies.length; i++) {
      const rule = result.condition_applies[i];
      if (rule.base !== 'prods' || rule.cond_base !== 'prods' || rule.cond_minimum !== 'quant') continue;
      const items = rule.product_items || [];
      for (let j = 0; j < items.length; j++) {
        if (!seen.has(items[j])) {
          seen.add(items[j]);
          productIds.push(items[j]);
        }
      }
    }

    if (!productIds.length) return [];

    const nodes = await getConditionalProductsByIds(productIds.slice(0, 8));
    if (!nodes || !nodes.length) return [];

    const candidates = [];
    for (let k = 0; k < nodes.length; k++) {
      const node = nodes[k];
      if (!node || !node.availableForSale) continue;

      const pid = parseInt(node.id.split('/').pop(), 10);
      const variantNode = node.variants && node.variants.nodes && node.variants.nodes[0];
      if (!variantNode) continue;
      const vid = parseInt(variantNode.id.split('/').pop(), 10);

      const priceAmount = parseFloat(node.priceRange.minVariantPrice.amount);
      const compareAmount = parseFloat(node.compareAtPriceRange.minVariantPrice.amount);
      const priceInCents = Math.round(priceAmount * 100);
      const prodSalePrice = !!(compareAmount && compareAmount > priceAmount);

      if (typeof co2.itemDiscountFull !== 'function') continue;

      const discData = await co2.itemDiscountFull(pid, vid, node.tags || [], priceInCents, 1, false, prodSalePrice);
      if (!discData || !(discData.condPerc > 0)) continue;

      const discountedPrice = Math.round(priceInCents - (priceInCents * discData.condPerc / 100) + Number.EPSILON);

      candidates.push({
        handle: node.handle,
        title: node.title,
        imageUrl: node.featuredImage ? node.featuredImage.url : '',
        price: priceInCents,
        condPerc: discData.condPerc,
        discountedPrice: discountedPrice,
      });
    }

    return candidates;
  }

  // ─── 4. Storefront API ─────────────────────────────────────

  async function getConditionalProductsByIds(pids) {
    const client = window.datoraGetStorefrontClient ? window.datoraGetStorefrontClient() : null;
    if (!client) return [];

    const query = 'query getProducts($ids:[ID!]!){nodes(ids:$ids){...on Product{id handle title availableForSale tags featuredImage{url(transform:{maxHeight:200,maxWidth:200})} priceRange{minVariantPrice{amount}} compareAtPriceRange{minVariantPrice{amount}} variants(first:1){nodes{id}}}}}';

    try {
      const domain = client.domain.indexOf('://') !== -1 ? client.domain : 'https://' + client.domain;
      const resp = await fetch(domain + '/api/' + client.version + '/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': client.token,
        },
        body: JSON.stringify({
          query: query,
          variables: { ids: pids.map(function (id) { return 'gid://shopify/Product/' + id; }) },
        }),
      });
      if (!resp.ok) return [];
      const json = await resp.json();
      return (json.data && json.data.nodes) ? json.data.nodes : [];
    } catch (e) {
      return [];
    }
  }

  // ─── 5. Rendering ──────────────────────────────────────────

  function renderConditionalModal(candidates, settings) {
    const modal = document.getElementById('conditional-discount-modal');
    if (!modal) return;

    let maxPerc = 0;
    for (let i = 0; i < candidates.length; i++) {
      if (candidates[i].condPerc > maxPerc) maxPerc = candidates[i].condPerc;
    }

    modal.querySelector('.conditional-modal__header').textContent =
      (settings.headerText || '').replace('[[PERCENT]]', String(maxPerc));

    if (flickityInstance) {
      try { flickityInstance.destroy(); } catch (e) {}
      flickityInstance = null;
    }

    const inner = modal.querySelector('.conditional-modal__products-inner');
    inner.innerHTML = '';
    for (let j = 0; j < candidates.length; j++) {
      inner.appendChild(renderConditionalCard(candidates[j], settings));
    }

    requestAnimationFrame(function () {
      initConditionalFlickity(inner);
    });
  }

  function renderConditionalCard(candidate, settings) {
    const url = window.Shopify.routes.root + 'products/' + candidate.handle;

    const card = document.createElement('div');
    card.className = 'conditional-modal__card';

    const imgLink = document.createElement('a');
    imgLink.href = url;
    imgLink.className = 'conditional-modal__card-link';
    if (candidate.imageUrl) {
      const img = document.createElement('img');
      img.className = 'conditional-modal__card-image';
      img.src = candidate.imageUrl;
      img.alt = candidate.title;
      img.loading = 'lazy';
      imgLink.appendChild(img);
    }
    card.appendChild(imgLink);

    const titleLink = document.createElement('a');
    titleLink.href = url;
    titleLink.className = 'conditional-modal__card-title';
    titleLink.textContent = candidate.title;
    card.appendChild(titleLink);

    const priceWrap = document.createElement('div');
    priceWrap.className = 'conditional-modal__card-price';

    const discSpan = document.createElement('span');
    discSpan.className = 'price price--highlight';
    discSpan.innerHTML = formatConditionalPrice(candidate.discountedPrice);
    priceWrap.appendChild(discSpan);

    const origSpan = document.createElement('span');
    origSpan.className = 'price price--compare';
    origSpan.innerHTML = formatConditionalPrice(candidate.price);
    priceWrap.appendChild(origSpan);

    const badge = document.createElement('span');
    badge.className = 'conditional-modal__card-badge';
    badge.textContent = '-' + candidate.condPerc + '%';
    priceWrap.appendChild(badge);

    card.appendChild(priceWrap);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'conditional-modal__card-btn';
    btn.textContent = settings.btnText;
    btn.setAttribute('data-upsell-trigger', '');
    btn.setAttribute('data-upsell-href', url);
    card.appendChild(btn);

    return card;
  }

  function formatConditionalPrice(cents) {
    if (window.datoraSafeFormatPrice) return window.datoraSafeFormatPrice(cents);
    return (cents / 100).toFixed(2).replace('.', ',') + ' \u20AC';
  }

  // ─── 6. Flickity ───────────────────────────────────────────

  async function initConditionalFlickity(el) {
    if (!el) return;

    if (!window.ThemeFlickity && !window.Flickity) {
      if (typeof LibraryLoader !== 'undefined') {
        await LibraryLoader.load('flickity');
      } else {
        return;
      }
    }

    const FlickityClass = window.ThemeFlickity || window.Flickity;
    if (!FlickityClass) return;

    flickityInstance = new FlickityClass(el, {
      contain: true,
      cellSelector: '.conditional-modal__card',
      cellAlign: 'left',
      pageDots: false,
      prevNextButtons: true,
      groupCells: true,
      draggable: '>1',
      freeScroll: true,
      wrapAround: false,
      percentPosition: false,
      rightToLeft: window.themeVariables && window.themeVariables.settings.direction === 'rtl',
    });

    let maxH = 0;
    const cards = el.querySelectorAll('.conditional-modal__card');
    for (let i = 0; i < cards.length; i++) {
      if (cards[i].offsetHeight > maxH) maxH = cards[i].offsetHeight;
    }
    if (maxH > 0) {
      for (let j = 0; j < cards.length; j++) cards[j].style.height = maxH + 'px';
      flickityInstance.resize();
    }

    if (flickityInstance.slides && flickityInstance.slides.length > 1) {
      flickityInstance.slides[flickityInstance.slides.length - 1].target += 24;
    }
  }

  // ─── 7. Modal open / close ─────────────────────────────────

  function openConditionalModal() {
    const modal = document.getElementById('conditional-discount-modal');
    if (!modal) return;

    const drawer = document.getElementById('mini-cart');
    if (drawer && drawer.focusTrap) try { drawer.focusTrap.pause(); } catch (e) {}
    modal.hidden = false;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { modal.classList.add('is-open'); });
    });
  }

  function closeConditionalModal(onClosed) {
    const modal = document.getElementById('conditional-discount-modal');
    if (!modal || modal.hidden) {
      if (typeof onClosed === 'function') onClosed();
      return;
    }
    const drawer = document.getElementById('mini-cart');
    if (drawer && drawer.focusTrap) try { drawer.focusTrap.unpause(); } catch (e) {}
    modal.classList.remove('is-open');
    modal.addEventListener('transitionend', function handler(e) {
      if (e.target !== modal) return;
      modal.removeEventListener('transitionend', handler);
      modal.hidden = true;
      if (typeof onClosed === 'function') onClosed();
    });
  }

  function openConditionalCart() {
    requestAnimationFrame(function () {
      const drawer = document.getElementById('mini-cart');
      if (drawer) drawer.open = true;
    });
  }

  // ─── 8. Event listeners ────────────────────────────────────

  const modalEl = document.getElementById('conditional-discount-modal');
  if (modalEl) {
    modalEl.addEventListener('click', function (e) {
      if (e.target.classList.contains('conditional-modal__overlay') || e.target.closest('[data-conditional-modal-close]')) {
        closeConditionalModal();
      }
    });
  }

  // Suppress automatic cart drawer opening while a conditional modal is pending/open.
  document.documentElement.addEventListener('cart:refresh', function (e) {
    if (wasOpen && e.detail) {
      e.detail.openMiniCart = false;
      wasOpen = false;
    }
  }, true);
});
